// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

/**
 * Payment API Types
 *
 * Request and response types for PILLAR payment vault endpoints
 */

import type {
    GatewayId } from './types/third-party-system.enums';
import type {
    PaymentTransactionStatus,
    PaymentTransactionLifecycleEventType,
    PaymentReconciliationStatus,
} from './payment-contracts';
import type { Status } from './identity-contracts';
import {
    AuthorizableAction,
    ResourceType } from './authorization-contracts';
import type { JsonValue } from './common.types';
import type { CursorPagination, OpaqueCursorPagination } from './authorization.types';
import type {
    DealertrackDealTransactionDetails,
    DealertrackLeasingTransactionDetails,
    DealertrackPartsTransactionDetails,
    DealertrackServiceTransactionDetails,
    DealertrackTransactionDetails,
} from './types/ipw/dealertrack/dealertrack-transaction-details';
import type { CredentialType } from './secret-contracts';
import type { BusinessDetails } from './backend_config.types';
import type { MerchantTimeZone } from './timezone.utils';
import {
    ThirdPartySystemId,
    type IpgSystemId,
    type SupportedIpgSystemId,
} from './types/third-party-system.enums';
import { TransactionDetailsTypeId } from './types/transaction-details.enums';
import type {
    SearchTermsRequest,
    StructuredSearchPredicatesRequest } from './omnisearch.types';
import type { Address } from './backend_config.types';
import type { IpgCardTokenizationResponse } from './types/ipg/ipg-card-tokenization-response-registry';
import type { IpgBrowserTokenizationSession } from './types/ipg/ipg-browser-tokenization-session-registry';
import {
    IPG_GATEWAY_TRANSACTION_DETAILS_TYPE,
    type IpgGatewayTransactionDetails,
    type IpgGatewayTransactionDetailsType,
} from './types/ipg/ipg-gateway-transaction-details-registry';
import type { GatewayLifecycleRequest } from './types/ipg/gateway-lifecycle-request.types';
import type {
    GatewayLifecycleResponseJsonBody } from './types/ipg/gateway-lifecycle-response.types';

export const CardType = {
    DEBIT: 'DEBIT',
    CREDIT: 'CREDIT',
    PREPAID: 'PREPAID',
    UNKNOWN: 'UNKNOWN',
} as const;

export type CardType = (typeof CardType)[keyof typeof CardType];

export type TransactionDetailsExtensionDiscriminant = TransactionDetailsTypeId;

export interface CustomerDetails {
    customerName?: string;
    mainPhone?: string;
    otherPhones: string[];
    mainEmail?: string;
    address?: Address;
    billingAddress?: Address;
}

export const CustomerDetailsStorageVersion = {
    V2: 2,
} as const;

export type CustomerDetailsStorageVersion =
    (typeof CustomerDetailsStorageVersion)[keyof typeof CustomerDetailsStorageVersion];

export type CustomerDetailsStorageV1 = CustomerDetails;

export interface CustomerDetailsStorageV2 {
    readonly customer_details_version: typeof CustomerDetailsStorageVersion.V2;
    readonly details: CustomerDetails;
}

export type StoredCustomerDetails = CustomerDetailsStorageV1
    | CustomerDetailsStorageV2;

export const PaymentTransactionConfigurationOverrideReason = {
    GATEWAY_FEE_MISMATCH_SURCHARGE_ONLY: 'gateway_fee_mismatch_surcharge_only',
    GATEWAY_FEE_MISMATCH_CONVENIENCE_FEE_ONLY: 'gateway_fee_mismatch_convenience_fee_only',
    GATEWAY_FEE_MISMATCH_AMBIGUOUS: 'gateway_fee_mismatch_ambiguous',
} as const;

export type PaymentTransactionConfigurationOverrideReason =
    (typeof PaymentTransactionConfigurationOverrideReason)[keyof typeof PaymentTransactionConfigurationOverrideReason];

export interface PaymentTransactionConfiguration {
    credential_id: number;
    credential_type: CredentialType;
    is_virtual: boolean | null;
    allow_surcharge: boolean;
    allow_convenience_fee: boolean;
    allow_override_fee: boolean;
    surcharge_pct: number | null;
    convenience_fee: number | null;
    processor_tid?: string;
    transaction_configuration_override?: boolean;
    transaction_configuration_override_reason?: PaymentTransactionConfigurationOverrideReason;
}

export const PaymentTransactionConfigurationStorageVersion = {
    V2: 2,
} as const;

export type PaymentTransactionConfigurationStorageVersion =
    (typeof PaymentTransactionConfigurationStorageVersion)[keyof typeof PaymentTransactionConfigurationStorageVersion];

export type PaymentTransactionConfigurationStorageV1 =
    PaymentTransactionConfiguration;

export interface PaymentTransactionConfigurationStorageV2 {
    readonly payment_transaction_configuration_version:
        typeof PaymentTransactionConfigurationStorageVersion.V2;
    readonly config: PaymentTransactionConfiguration;
}

export type StoredPaymentTransactionConfiguration =
    | PaymentTransactionConfigurationStorageV1
    | PaymentTransactionConfigurationStorageV2;

export const GatewayTransactionDetailsType = IPG_GATEWAY_TRANSACTION_DETAILS_TYPE;

export type GatewayTransactionDetailsType = IpgGatewayTransactionDetailsType;

export interface CoreTransactionDetails extends Omit<CustomerDetails, 'otherPhones'> {
    amount: number;
    tax?: number;
    fee?: number;
    surcharge?: number;
    detectedAmount?: number;
    currency: string;
    merchant_transaction_id?: string;
    description?: string;
    notes?: string;
    ipw_customer_id?: string;
    otherPhones?: string[];
}
export interface BaseIpwTransactionDetails extends CoreTransactionDetails {
    ipw_system_id: ThirdPartySystemId;
    transaction_details_type_id: TransactionDetailsTypeId;
    transaction_details_type_version: number;
}

export interface GenericIpwTransactionDetailsDynamicField {
    readonly value: JsonValue;
    readonly display_name?: string;
    readonly source_element_key?: string;
}

export type GenericIpwTransactionDetailsDynamicFields = Record<
    string,
    GenericIpwTransactionDetailsDynamicField
>;

export interface GenericIpwTransactionDetails extends CoreTransactionDetails {
    ipw_system_id: typeof ThirdPartySystemId.GENERIC;
    transaction_details_type_id: typeof TransactionDetailsTypeId.GENERIC;
    transaction_details_type_version: 0;
    rbs_dynamic_fields?: GenericIpwTransactionDetailsDynamicFields;
}

export type GenericTransactionDetails = GenericIpwTransactionDetails;

export type IpwTransactionDetails = DealertrackTransactionDetails;
// | PcPartPickerTransactionDetails
// | ...

export type TransactionDetails = GenericIpwTransactionDetails
    | IpwTransactionDetails;

export const PaymentTransactionDetailsStorageVersion = {
    V2: 2,
} as const;

export type PaymentTransactionDetailsStorageVersion =
    (typeof PaymentTransactionDetailsStorageVersion)[keyof typeof PaymentTransactionDetailsStorageVersion];

export type PaymentTransactionDetailsStorageV1 = TransactionDetails;

export interface PaymentTransactionDetailsStorageV2 {
    readonly payment_transaction_details_version:
        typeof PaymentTransactionDetailsStorageVersion.V2;
    readonly details: TransactionDetails;
}

export type StoredPaymentTransactionDetails =
    | PaymentTransactionDetailsStorageV1
    | PaymentTransactionDetailsStorageV2;

/**
 * Reuse TransactionDetails for payment-request details until a request-specific
 * type family is truly needed.
 */
export type PaymentRequestDetails = TransactionDetails;

export const PaymentRequestDetailsStorageVersion = {
    V2: 2,
} as const;

export type PaymentRequestDetailsStorageVersion =
    (typeof PaymentRequestDetailsStorageVersion)[keyof typeof PaymentRequestDetailsStorageVersion];

export type PaymentRequestDetailsStorageV1 = PaymentRequestDetails;

export interface PaymentRequestDetailsStorageV2 {
    readonly payment_request_details_version:
        typeof PaymentRequestDetailsStorageVersion.V2;
    readonly details: PaymentRequestDetails;
}

export type StoredPaymentRequestDetails =
    | PaymentRequestDetailsStorageV1
    | PaymentRequestDetailsStorageV2;

export type DealertrackPartsPaymentRequestSourceDocumentKey = Pick<
    DealertrackPartsTransactionDetails,
    'transaction_details_type_id' | 'transaction_details_type_version' | 'partsInvoiceNumber'
>;

export type DealertrackServicePaymentRequestSourceDocumentKey = Pick<
    DealertrackServiceTransactionDetails,
    'transaction_details_type_id' | 'transaction_details_type_version' | 'roNumber'
>;

export type DealertrackDealPaymentRequestSourceDocumentKey = Pick<
    DealertrackDealTransactionDetails,
    'transaction_details_type_id' | 'transaction_details_type_version' | 'stockNumber'
>;

export type DealertrackLeasingPaymentRequestSourceDocumentKey = Pick<
    DealertrackLeasingTransactionDetails,
    'transaction_details_type_id' | 'transaction_details_type_version' | 'stockNumber'
>;

export type DealertrackPaymentRequestSourceDocumentKey =
    | DealertrackPartsPaymentRequestSourceDocumentKey
    | DealertrackServicePaymentRequestSourceDocumentKey
    | DealertrackDealPaymentRequestSourceDocumentKey
    | DealertrackLeasingPaymentRequestSourceDocumentKey;

export interface PaymentRequestSourceDocumentKeyByTransactionDetailsType {
    [TransactionDetailsTypeId.DEALERTRACK_PARTS]: DealertrackPartsPaymentRequestSourceDocumentKey;
    [TransactionDetailsTypeId.DEALERTRACK_SERVICE]: DealertrackServicePaymentRequestSourceDocumentKey;
    [TransactionDetailsTypeId.DEALERTRACK_DEAL]: DealertrackDealPaymentRequestSourceDocumentKey;
    [TransactionDetailsTypeId.DEALERTRACK_LEASING]: DealertrackLeasingPaymentRequestSourceDocumentKey;
}

export type PaymentRequestSourceDocumentKey =
    PaymentRequestSourceDocumentKeyByTransactionDetailsType[
        keyof PaymentRequestSourceDocumentKeyByTransactionDetailsType
    ];

export const PaymentRequestSourceDocumentKeyStorageVersion = {
    V2: 2,
} as const;

export type PaymentRequestSourceDocumentKeyStorageVersion =
    (typeof PaymentRequestSourceDocumentKeyStorageVersion)[keyof typeof PaymentRequestSourceDocumentKeyStorageVersion];

export type PaymentRequestSourceDocumentKeyStorageV1 =
    PaymentRequestSourceDocumentKey;

export interface PaymentRequestSourceDocumentKeyStorageV2 {
    readonly payment_request_source_document_key_version:
        typeof PaymentRequestSourceDocumentKeyStorageVersion.V2;
    readonly source_document_key: PaymentRequestSourceDocumentKey;
}

export type StoredPaymentRequestSourceDocumentKey =
    | PaymentRequestSourceDocumentKeyStorageV1
    | PaymentRequestSourceDocumentKeyStorageV2;

export const PaymentRail = {
    CREDIT_CARD: 'CREDIT_CARD',
    DEBIT_CARD: 'DEBIT_CARD',
    ACH: 'ACH',
} as const;

export type PaymentRail = (typeof PaymentRail)[keyof typeof PaymentRail];

export interface CreditCardPaymentMethodTokenDetails {
    payment_rail: typeof PaymentRail.CREDIT_CARD;
    masked_pan: string;
    card_type: CardType;
    card_brand: string;
    cardholder_name: string;
    expiry_date?: string; // MM/YY
}

export interface DebitCardPaymentMethodTokenDetails {
    payment_rail: typeof PaymentRail.DEBIT_CARD;
    masked_pan: string;
    card_type: CardType;
    card_brand: string;
    cardholder_name: string;
    expiry_date?: string; // MM/YY
}

export type CardPaymentMethodTokenDetails =
    | CreditCardPaymentMethodTokenDetails
    | DebitCardPaymentMethodTokenDetails;

export interface AchPaymentMethodTokenDetails {
    payment_rail: typeof PaymentRail.ACH;
    account_holder_name: string;
    masked_account_number: string;
    masked_routing_number: string;
    bank_name?: string;
}

export type PaymentMethodTokenDetails =
    | CardPaymentMethodTokenDetails
    | AchPaymentMethodTokenDetails;

export const PaymentMethodTokenDetailsStorageVersion = {
    V2: 2,
} as const;

export type PaymentMethodTokenDetailsStorageVersion =
    (typeof PaymentMethodTokenDetailsStorageVersion)[keyof typeof PaymentMethodTokenDetailsStorageVersion];

export type PaymentMethodTokenDetailsStorageV1 = PaymentMethodTokenDetails;

export interface PaymentMethodTokenDetailsStorageV2 {
    readonly payment_method_token_details_version:
        typeof PaymentMethodTokenDetailsStorageVersion.V2;
    readonly details: PaymentMethodTokenDetails;
}

export type StoredPaymentMethodTokenDetails =
    | PaymentMethodTokenDetailsStorageV1
    | PaymentMethodTokenDetailsStorageV2;

export const DeliveryChannel = {
    EMAIL: 'EMAIL',
    SMS: 'SMS',
} as const;

export type DeliveryChannel = (typeof DeliveryChannel)[keyof typeof DeliveryChannel];

export const DeliveryProvider = {
    TWILIO: 'TWILIO',
    SENDGRID: 'SENDGRID',
    LOCAL_FILE: 'LOCAL_FILE',
} as const;

export type DeliveryProvider = (typeof DeliveryProvider)[keyof typeof DeliveryProvider];

export type PaymentRequestDeliveryAcceptedProviderPayload = Record<
    string,
    string
>;

export type StoredTwilioStatusCallbackPayload = Record<
    string,
    string | readonly string[]
>;

export type StoredSendgridEventWebhookEvent = Record<string, JsonValue>;

export type PaymentRequestDeliveryEventPayload =
    | PaymentRequestDeliveryAcceptedProviderPayload
    | StoredTwilioStatusCallbackPayload
    | StoredSendgridEventWebhookEvent;

export interface TwilioAcceptedProviderPayloadRow {
    readonly delivery_channel: typeof DeliveryChannel.SMS;
    readonly delivery_provider: typeof DeliveryProvider.TWILIO;
    readonly provider_event_type: 'accepted';
    readonly provider_payload: PaymentRequestDeliveryAcceptedProviderPayload;
}

export interface SendgridAcceptedProviderPayloadRow {
    readonly delivery_channel: typeof DeliveryChannel.EMAIL;
    readonly delivery_provider: typeof DeliveryProvider.SENDGRID;
    readonly provider_event_type: 'accepted';
    readonly provider_payload: PaymentRequestDeliveryAcceptedProviderPayload;
}

export type PaymentRequestDeliveryAcceptedProviderPayloadRow =
    | TwilioAcceptedProviderPayloadRow
    | SendgridAcceptedProviderPayloadRow;

export interface TwilioStatusCallbackProviderPayloadRow {
    readonly delivery_channel: typeof DeliveryChannel.SMS;
    readonly delivery_provider: typeof DeliveryProvider.TWILIO;
    readonly provider_event_type: string;
    readonly provider_payload: StoredTwilioStatusCallbackPayload;
}

export interface SendgridEventWebhookProviderPayloadRow {
    readonly delivery_channel: typeof DeliveryChannel.EMAIL;
    readonly delivery_provider: typeof DeliveryProvider.SENDGRID;
    readonly provider_event_type: string;
    readonly provider_payload: StoredSendgridEventWebhookEvent;
}

export type PaymentRequestDeliveryProviderPayloadRow =
    | PaymentRequestDeliveryAcceptedProviderPayloadRow
    | TwilioStatusCallbackProviderPayloadRow
    | SendgridEventWebhookProviderPayloadRow;

export const PaymentRequestStatus = {
    DRAFT: 'DRAFT',
    SENT: 'SENT',
    VIEWED: 'VIEWED',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    PAID: 'PAID',
    EXPIRED: 'EXPIRED',
    CANCELED: 'CANCELED',
    REISSUED: 'REISSUED',
} as const;

export type PaymentRequestStatus = (typeof PaymentRequestStatus)[keyof typeof PaymentRequestStatus];

export const GatewayOperationMnemonic = {
    SEND_TO_TERMINAL: 'send_to_terminal',
    VOID_TRANSACTION: 'void_transaction',
    REFUND_CARD_PAYMENT: 'refund_card_payment',
    REFUND_TERMINAL_PAYMENT: 'refund_terminal_payment',
    PROCESS_CARD_PAYMENT: 'process_card_payment',
    PROCESS_ACH_PAYMENT: 'process_ach_payment',
    RECORD_TOKEN: 'record_token',
} as const;

export type GatewayOperationMnemonic =
    (typeof GatewayOperationMnemonic)[keyof typeof GatewayOperationMnemonic];

export const GatewayTransactionType = {
    ACH_PAYMENT: 'ach_payment',
    CARD_NOT_PRESENT_PAYMENT: 'card_not_present_payment',
    CARD_NOT_PRESENT_TOKEN_PAYMENT: 'card_not_present_token_payment',
    CARD_TERMINAL_PAYMENT: 'card_terminal_payment',
    CARD_TOKEN: 'card_token',
} as const;

export type GatewayTransactionType =
    (typeof GatewayTransactionType)[keyof typeof GatewayTransactionType];

export const PaymentTransactionFlowType = {
    TERMINAL: 'terminal',
    CARD_NOT_PRESENT: 'card_not_present',
    E_INVOICE: 'e_invoice',
    ACH: 'ach',
} as const;

export type PaymentTransactionFlowType =
    (typeof PaymentTransactionFlowType)[keyof typeof PaymentTransactionFlowType];

export const PaymentGatewayTransactionTypes: ReadonlyArray<GatewayTransactionType> = [
    GatewayTransactionType.CARD_NOT_PRESENT_PAYMENT,
    GatewayTransactionType.CARD_NOT_PRESENT_TOKEN_PAYMENT,
    GatewayTransactionType.CARD_TERMINAL_PAYMENT,
];

/**
 * Transaction-details payload accepted on payment create/write flows.
 *
 * The canonical merchant_transaction_id may already be known at IPW-detection time,
 * so write contracts must preserve it when present rather than erasing it.
 */
export type TransactionDetailsCreateRequest<InputType extends TransactionDetails> = InputType;

export interface PaymentTransactionCreateData {
    merchantOrganizationId: number;
    terminalDeviceid: number;
    transactionConfiguration: PaymentTransactionConfiguration;
    customerId: number | undefined;
    paymentMethodTokenId: number | null;
    gatewayId: GatewayId;
    gatewayTransactionType: GatewayTransactionType;
    transactionDetails: TransactionDetailsCreateRequest<TransactionDetails>;
    gatewayTxnId?: string;
    gatewayBatch?: string;
    gatewayAuthCode?: string;
    gatewayStan?: string;
    gatewayRrn?: string;
    cardBrand?: string;
    cardType?: CardType;
    cardLast4?: string;
    cardholderName?: string;
    status?: PaymentTransactionStatus;
    batchedAmount?: number;
    refundedAmount?: number;
}

export interface CardTokenCreateData {
    holderOrganizationId: number;
    terminalDeviceid: number;
    customerId: number | undefined;
    gatewayId: GatewayId;
    gatewayToken: string;
    cardBrand: string;
    cardType: CardType;
    cardMaskedPan: string;
    cardholderName: string;
    customerDetails?: CustomerDetails;
    createdByTransactionId?: number;
    storedForFutureUse?: boolean;
}

export interface CoreGatewayTransactionDetails {
    gateway_id: GatewayId;
    gateway_transaction_details_type: GatewayTransactionDetailsType;
    gateway_transaction_details_version: number;
    gateway_txn_id: string;
    base_amount: number;
    total_amount: number;
    total_fee_amount: number;
    total_tax_amount: number;
    is_void: boolean;
    is_settled: boolean;
    refund_amount: number;
    refund_count: number;
    card_brand?: string;
    card_last4?: string;
    cardholder_name?: string;
    card_type?: CardType;
}

export type GatewayTransactionDetails = IpgGatewayTransactionDetails;

export interface GatewayTransactionDetailsFetchResult {
    gateway_transaction_details: GatewayTransactionDetails;
    gateway_response: JsonValue;
    gateway_response_type: string;
    gateway_response_status: number;
    gateway_response_header: Record<string, string>;
    gateway_endpoint_url?: string;
}

/**
 * Strict create contract for flows where PILLAR/DB owns merchant_transaction_id.
 */
export type PaymentTransactionSystemOwnedCreateData = PaymentTransactionCreateData;

export interface PaymentTransactionAmountValue {
    toString(): string;
    toNumber(): number;
}

export interface PaymentTransactionLifecycleEventWithDetails {
    payment_transaction_lifecycle_event_id: number;
    payment_transaction_id: number;
    event_type: PaymentTransactionLifecycleEventType;
    event_start: Date;
    event_end: Date;
    event_ts?: Date;
    amount?: string | null;
    amounts?: JsonValue | null;
    gateway_request?: GatewayLifecycleRequest | null;
    gateway_response_http_status?: number | null;
    gateway_response_header?: JsonValue | null;
    gateway_response_json?: JsonValue | null;
    gateway_response_raw?: string | null;
    gateway_request_type?: string | null;
    gateway_response_type?: string | null;
    gateway_operation_mnemonic?: string | null;
    gateway_endpoint_url?: string | null;
    pillar_request?: JsonValue | null;
    pillar_response?: JsonValue | null;
    error_code?: string | null;
    error_message?: string | null;
}

export interface PaymentTransactionWithRelationsContract<
    PaymentMethodTokenType = unknown,
    LifecycleEventType = PaymentTransactionLifecycleEventWithDetails,
    AmountType = PaymentTransactionAmountValue,
    StatusType = PaymentTransactionStatus,
> {
    payment_transaction_id: number;
    merchant_organization_id: number;
    payment_method_token_id: number | null;
    gateway_id: number;
    gateway_transaction_type: GatewayTransactionType;
    gateway_txn_id: string | null;
    gateway_batch: string | null;
    gateway_auth_code: string | null;
    gateway_stan: string | null;
    gateway_rrn: string | null;
    original_amount: AmountType;
    batched_amount: AmountType | null;
    refunded_amount: AmountType | null;
    status: StatusType;
    ipw_system_id: number;
    ipw_customer_id: string | null;
    ipw_transaction_details: JsonValue | null;
    merchant_transaction_id: string;
    payment_method_token: PaymentMethodTokenType | null;
    lifecycle_events: LifecycleEventType[];
}

export interface PillarPaymentRequestTypeMap {
    readonly TERMINAL_SALE: 'card_terminal_payment';
    readonly TERMINAL_ABORT: 'terminal_abort';
    readonly VOID_TRANSACTION: 'void_transaction';
    readonly REFUND_TRANSACTION: 'refund_transaction';
    readonly ACH_PAYMENT: 'ach_payment';
    readonly RECORD_TOKEN: 'record_token';
    readonly PROCESS_CARD_TOKEN_PAYMENT: 'process_card_token_payment';
    readonly PROCESS_CARD_NOT_PRESENT_PAYMENT: 'process_card_not_present_payment';
    readonly GET_OPEN_BATCH: 'get_open_batch';
    readonly QUERY_TRANSACTIONS: 'query_transactions';
    readonly GET_TRANSACTION_STATUS: 'get_transaction_status';
    readonly GET_VAULT_TRANSACTIONS: 'get_vault_transactions';
    readonly GET_TRANSACTION_DETAILS: 'get_transaction_details';
    readonly RECORD_RECONCILIATION: 'record_reconciliation';
    readonly PAYMENT_AUDIT_LOG: 'payment_audit_log';
}

export const PillarPaymentRequestType: PillarPaymentRequestTypeMap = {
    TERMINAL_SALE: 'card_terminal_payment',
    TERMINAL_ABORT: 'terminal_abort',
    VOID_TRANSACTION: 'void_transaction',
    REFUND_TRANSACTION: 'refund_transaction',
    ACH_PAYMENT: 'ach_payment',
    RECORD_TOKEN: 'record_token',
    PROCESS_CARD_TOKEN_PAYMENT: 'process_card_token_payment',
    PROCESS_CARD_NOT_PRESENT_PAYMENT: 'process_card_not_present_payment',
    GET_OPEN_BATCH: 'get_open_batch',
    QUERY_TRANSACTIONS: 'query_transactions',
    GET_TRANSACTION_STATUS: 'get_transaction_status',
    GET_VAULT_TRANSACTIONS: 'get_vault_transactions',
    GET_TRANSACTION_DETAILS: 'get_transaction_details',
    RECORD_RECONCILIATION: 'record_reconciliation',
    PAYMENT_AUDIT_LOG: 'payment_audit_log',
};

export type PillarPaymentRequestType =
    PillarPaymentRequestTypeMap[keyof PillarPaymentRequestTypeMap];

export interface PillarPaymentResponseTypeMap {
    readonly TERMINAL_SALE: 'card_terminal_payment';
    readonly TERMINAL_ABORT: 'terminal_abort';
    readonly VOID_TRANSACTION: 'void_transaction';
    readonly REFUND_TRANSACTION: 'refund_transaction';
    readonly ACH_PAYMENT: 'ach_payment';
    readonly RECORD_TOKEN: 'record_token';
    readonly PROCESS_CARD_TOKEN_PAYMENT: 'process_card_token_payment';
    readonly PROCESS_CARD_NOT_PRESENT_PAYMENT: 'process_card_not_present_payment';
    readonly GET_OPEN_BATCH: 'get_open_batch';
    readonly QUERY_TRANSACTIONS: 'query_transactions';
    readonly GET_TRANSACTION_STATUS: 'get_transaction_status';
    readonly GET_VAULT_TRANSACTIONS: 'get_vault_transactions';
    readonly GET_TRANSACTION_DETAILS: 'get_transaction_details';
    readonly RECORD_RECONCILIATION: 'record_reconciliation';
    readonly PAYMENT_AUDIT_LOG: 'payment_audit_log';
}

export const PillarPaymentResponseType: PillarPaymentResponseTypeMap = {
    TERMINAL_SALE: 'card_terminal_payment',
    TERMINAL_ABORT: 'terminal_abort',
    VOID_TRANSACTION: 'void_transaction',
    REFUND_TRANSACTION: 'refund_transaction',
    ACH_PAYMENT: 'ach_payment',
    RECORD_TOKEN: 'record_token',
    PROCESS_CARD_TOKEN_PAYMENT: 'process_card_token_payment',
    PROCESS_CARD_NOT_PRESENT_PAYMENT: 'process_card_not_present_payment',
    GET_OPEN_BATCH: 'get_open_batch',
    QUERY_TRANSACTIONS: 'query_transactions',
    GET_TRANSACTION_STATUS: 'get_transaction_status',
    GET_VAULT_TRANSACTIONS: 'get_vault_transactions',
    GET_TRANSACTION_DETAILS: 'get_transaction_details',
    RECORD_RECONCILIATION: 'record_reconciliation',
    PAYMENT_AUDIT_LOG: 'payment_audit_log',
};

export type PillarPaymentResponseType =
    PillarPaymentResponseTypeMap[keyof PillarPaymentResponseTypeMap];

/**
 * Terminal Sale Request
 * Sends a transaction to a Valor physical terminal
 */
export interface TerminalSaleRequest {
    terminal_device_uuid: string;
    amount?: string; // Deprecated: use transaction_details.amount
    description?: string; // Deprecated: use transaction_details.description
    customer_name?: string; // Deprecated: use transaction_details.customerName
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
}

/**
 * Strict terminal-sale contract for flows where PILLAR/DB owns merchant_transaction_id.
 */
export type TerminalSaleRequestSystemOwned = TerminalSaleRequest;

export type TokenizationResult =
    | {
          payment_method_token_uuid: string;
          error?: never;
      }
    | {
          error: string;
          payment_method_token_uuid?: never;
      };

/**
 * Terminal Sale Response
 */
export interface TerminalSaleResponse {
    payment_transaction_uuid?: string;
    gateway_txn_id?: string; // Gateway transaction ID (if available)
    success: boolean;
    error?: string; // Error message if transaction failed
    tokenization_result?: TokenizationResult;
}

export interface TerminalTokenizationRequest {
    terminal_device_uuid: string;
    card_type: CardType;
    card_brand: string;
    masked_pan: string;
    cardholder_name: string;
    customer_details: CustomerDetails;
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
}

export type TerminalTokenizationResponse =
    | {
          success: true;
          payment_transaction_uuid: string;
          payment_method_token_uuid: string;
      }
    | {
          success: false;
          payment_transaction_uuid: string;
          error: string;
      };

/**
 * Terminal Abort Request
 * Aborts a pending terminal transaction
 */
export interface TerminalAbortRequest {
    terminal_device_uuid: string;
}

/**
 * Terminal Abort Response
 */
export interface TerminalAbortResponse {
    success: boolean;
    error?: string;
}

/**
 * Void Transaction Request
 * Voids a transaction (full or partial)
 */
export interface VoidTransactionRequest {
    payment_transaction_uuid: string;
    account_id?: string; // Mnemonic ID (account_id) - optional, will be extracted from transaction if not provided
}

/**
 * Void Transaction Response
 */
export interface VoidTransactionResponse {
    success: boolean;
    voided_amount: string;
    remaining_batched_amount: string;
    error?: string;
}

/**
 * Refund Transaction Request
 * Refunds a transaction (full or partial)
 */
export interface RefundTransactionRequest {
    payment_transaction_uuid: string;
    amount: string; // Refund amount
    account_id?: string; // Mnemonic ID (account_id) - optional, will be extracted from transaction if not provided
}

/**
 * Refund Transaction Response
 */
export interface RefundTransactionResponse {
    success: boolean;
    refunded_amount: string; // Amount refunded in this operation
    cumulative_refunded_amount: string; // Total refunded amount (cumulative)
    error?: string;
}

export interface ResetTransactionReconciliationResponse {
    success: boolean;
    reconciled: PaymentReconciliationStatus;
    payment_transaction_lifecycle_event_uuid: string;
}

export interface ResetPaymentRequestReconciliationResponse {
    success: boolean;
    reconciled: PaymentReconciliationStatus;
    payment_transaction_lifecycle_event_uuids: string[];
}

export const PaymentRequestTransactionOperationOutcome = {
    SUCCESS: 'SUCCESS',
    SKIPPED: 'SKIPPED',
    FAILED: 'FAILED',
} as const;

export type PaymentRequestTransactionOperationOutcome =
    (typeof PaymentRequestTransactionOperationOutcome)[keyof typeof PaymentRequestTransactionOperationOutcome];

export interface VoidPaymentRequestTransactionResult {
    payment_transaction_uuid: string;
    merchant_transaction_id?: string;
    outcome: PaymentRequestTransactionOperationOutcome;
    voided_amount: string;
    remaining_batched_amount: string;
    message?: string;
}

export interface VoidPaymentRequestResponse {
    success: boolean;
    transaction_results: VoidPaymentRequestTransactionResult[];
}

export interface RefundPaymentRequestRequest {
    amount: string;
}

export interface RefundPaymentRequestTransactionResult {
    payment_transaction_uuid: string;
    merchant_transaction_id?: string;
    outcome: PaymentRequestTransactionOperationOutcome;
    requested_amount: string;
    refunded_amount: string;
    cumulative_refunded_amount: string;
    message?: string;
}

export interface RefundPaymentRequestResponse {
    success: boolean;
    requested_amount: string;
    refunded_amount: string;
    remaining_unfulfilled_amount: string;
    transaction_results: RefundPaymentRequestTransactionResult[];
}

/**
 * Record Online Payment Request
 * Records a successful card/ACH payment executed via IPG workflows
 */
export interface RecordTokenRequest {
    terminal_device_uuid: string;
    stored_for_future_use?: boolean;
    card_type: CardType;
    card_brand: string;
    masked_pan: string;
    cardholder_name: string;
    gateway_response: GatewayCardTokenizationResponse;
    customer_details: CustomerDetails;
}

/**
 * Record Online Payment Response
 */
export interface RecordTokenResponse {
    success: boolean;
    payment_method_token_uuid: string;
}

export type GatewayCardTokenizationResponse = IpgCardTokenizationResponse;

/**
 * Process Card Token Payment Request
 * Processes a card payment using a gateway token via PILLAR
 */
export interface ProcessCardTokenPaymentRequest {
    terminal_device_uuid: string;
    token: string; // Gateway token for card on file
    card_type: CardType;
    pan_prefix?: string;
    card_brand: string;
    card_last4: string;
    cardholder_name: string;
    expiry_date?: string;
    billing_address?: Address;
    amount?: string; // Deprecated: use transaction_details.amount
    description?: string; // Deprecated: use transaction_details.description
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
    surcharge_amount: number;
    convenience_fee_amount: number;
    total_fee_amount: number;
    total_amount_paid: number;
}

export type SubmitCardTokenPaymentRequest = Omit<
    ProcessCardTokenPaymentRequest,
    'card_type' | 'pan_prefix'
> & {
    pan_prefix: string;
};

/**
 * Merchant-scoped stored-card payment request.
 * Provider tokens and card metadata are resolved by the backend.
 */
export interface ProcessStoredPaymentMethodPaymentRequest {
    terminal_device_uuid: string;
    payment_method_token_uuid: string;
    billing_address?: Address;
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
    surcharge_amount: number;
    convenience_fee_amount: number;
    total_fee_amount: number;
    total_amount_paid: number;
}

/**
 * Strict card-token contract for flows where PILLAR/DB owns merchant_transaction_id.
 */
export type ProcessCardTokenPaymentRequestSystemOwned = ProcessCardTokenPaymentRequest;

/**
 * Guest-scoped tokenized card-payment request.
 * Immutable routing, amount, and transaction details are derived server-side from the
 * active PAYMENT_REQUEST_ACCESS token scope and the linked PaymentRequest.
 */
export interface GuestProcessCardTokenPaymentRequest {
    token: string;
    pan_prefix: string;
    card_brand: string;
    card_last4: string;
    cardholder_name: string;
    billing_address?: Address;
    receipt_email?: string;
    surcharge_amount: number;
    convenience_fee_amount: number;
    total_fee_amount: number;
    total_amount_paid: number;
}

export interface AuthenticatedProcessCardTokenPaymentRequest
    extends GuestProcessCardTokenPaymentRequest {
    stored_for_future_use: boolean;
}

export interface GuestProcessStoredPaymentMethodPaymentRequest {
    payment_method_token_uuid: string;
    receipt_email?: string;
}

export interface GuestBinLookupRequest {
    pan_prefix: string;
}

/**
 * Process Card Token Payment Response
 */
export interface ProcessCardTokenPaymentResponse {
    payment_transaction_uuid?: string;
    gateway_txn_id?: string;
    success: boolean;
    error?: string;
}

export interface ProcessCardNotPresentPaymentResponse
    extends ProcessCardTokenPaymentResponse {
    tokenization_result?: TokenizationResult;
}

/** Redacted durable record of a direct card-not-present payment request. */
export interface ProcessCardNotPresentPaymentLifecycleRequest {
    terminal_device_uuid: string;
    pan_prefix: string;
    card_type: CardType;
    card_brand: string;
    card_last4: string;
    cardholder_name: string;
    expiry_date?: string;
    billing_address?: Address;
    customer_details: CustomerDetails;
    stored_for_future_use: boolean;
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
    surcharge_amount: number;
    convenience_fee_amount: number;
    total_fee_amount: number;
    total_amount_paid: number;
}

export interface GetPaymentConfigurationRequest {
    terminal_device_uuid: string;
}

export interface PaymentConfigurationResponse {
    account_id: string;
    credential_uuid: string;
    gateway_id: GatewayId;
    credential_type: CredentialType;
    is_virtual: boolean | null;
    allow_surcharge: boolean;
    allow_convenience_fee: boolean;
    allow_override_fee: boolean;
    addressRequiredForCardPayment: boolean;
    surcharge_pct: number | null;
    convenience_fee: number | null;
}

export const PaymentActionAvailabilityStatus = {
    AVAILABLE: 'AVAILABLE',
    UNAVAILABLE: 'UNAVAILABLE',
} as const;

export type PaymentActionAvailabilityStatus =
    (typeof PaymentActionAvailabilityStatus)[keyof typeof PaymentActionAvailabilityStatus];

export const PaymentActionUnavailableReason = {
    CREDENTIAL_UNAVAILABLE: 'CREDENTIAL_UNAVAILABLE',
    CARD_ENTRY_UNAVAILABLE: 'CARD_ENTRY_UNAVAILABLE',
    STORED_CARD_UNAVAILABLE: 'STORED_CARD_UNAVAILABLE',
    TERMINAL_UNAVAILABLE: 'TERMINAL_UNAVAILABLE',
    PROVIDER_UNSUPPORTED: 'PROVIDER_UNSUPPORTED',
} as const;

export type PaymentActionUnavailableReason =
    (typeof PaymentActionUnavailableReason)[keyof typeof PaymentActionUnavailableReason];

export interface AvailablePaymentAction {
    status: typeof PaymentActionAvailabilityStatus.AVAILABLE;
}

export interface UnavailablePaymentAction {
    status: typeof PaymentActionAvailabilityStatus.UNAVAILABLE;
    reason: PaymentActionUnavailableReason;
    message: string;
}

export type PaymentActionAvailability = AvailablePaymentAction
    | UnavailablePaymentAction;

export interface PaymentActionReadinessResponse {
    cardEntry: PaymentActionAvailability;
    storedCard: PaymentActionAvailability;
    terminalSale: PaymentActionAvailability;
}

export interface CardPaymentTerminalMenuItem {
    terminal_device_uuid: string;
    terminal_device_name: string | null;
    logical_terminal_uuid: string;
    merchant_account_uuid: string;
    processor_name: string;
    processor_mid: string;
    processor_tid: string;
    processor_device_id: string;
    account_id: string;
    credential_uuid: string;
    organization_uuid: string;
    credential_type: CredentialType;
    system_id: SupportedIpgSystemId;
    credential_is_active: boolean;
    credential_applies_to_children: boolean;
    effective_allow_override_fee: boolean;
    addressRequiredForCardPayment: boolean;
    can_override_fee: boolean;
    effective_surcharge_pct: number | null;
    effective_surcharge_defined_by: PaymentFeeDefinedBy;
    effective_convenience_fee: number | null;
    effective_convenience_fee_defined_by: PaymentFeeDefinedBy;
    display_label: string;
}

export const PaymentTerminalOperationAction = {
    TAKE_PAYMENT: 'TAKE_PAYMENT',
    VOID_PAYMENT: 'VOID_PAYMENT',
    REFUND_PAYMENT: 'REFUND_PAYMENT',
} as const;

export type PaymentTerminalOperationAction =
    (typeof PaymentTerminalOperationAction)[keyof typeof PaymentTerminalOperationAction];

export const SelectableTerminalAuthorizationAction = {
    ...PaymentTerminalOperationAction,
    CREATE: AuthorizableAction.CREATE,
} as const;

export type SelectableTerminalAuthorizationAction =
    (typeof SelectableTerminalAuthorizationAction)[keyof typeof SelectableTerminalAuthorizationAction];

export const AccessibleTerminalAuthorizationAction = {
    TAKE_PAYMENT: AuthorizableAction.TAKE_PAYMENT,
    CREATE: AuthorizableAction.CREATE,
} as const;

export type AccessibleTerminalAuthorizationAction =
    (typeof AccessibleTerminalAuthorizationAction)[keyof typeof AccessibleTerminalAuthorizationAction];

export const AccessibleTerminalAuthorizationResourceType = {
    TERMINAL_DEVICE: ResourceType.TERMINAL_DEVICE,
    PAYMENT_REQUEST: ResourceType.PAYMENT_REQUEST,
} as const;

export type AccessibleTerminalAuthorizationResourceType =
    (typeof AccessibleTerminalAuthorizationResourceType)[keyof typeof AccessibleTerminalAuthorizationResourceType];

export interface ListCardPaymentTerminalDevicesRequest {
    system_id?: SupportedIpgSystemId;
    is_virtual: boolean;
    action: SelectableTerminalAuthorizationAction;
    resource_type?: AccessibleTerminalAuthorizationResourceType;
    credential_type?: CredentialType;
    limit?: number;
    cursor?: string;
    include_empty_reason?: boolean;
}

export const SelectableTerminalAccessFailureReason = {
    NO_TERMINAL_DEVICES_CONFIGURED: 'no_terminal_devices_configured',
    NO_LINKED_CREDENTIALS_CONFIGURED: 'no_linked_credentials_configured',
    MISSING_CREDENTIAL_PERMISSION: 'missing_credential_permission',
    MISSING_TERMINAL_PERMISSION: 'missing_terminal_permission',
    MISSING_PAYMENT_REQUEST_CREATE_PERMISSION:
        'missing_payment_request_create_permission',
    MISSING_CREDENTIAL_AND_TERMINAL_PERMISSIONS:
        'missing_credential_and_terminal_permissions',
} as const;

export type SelectableTerminalAccessFailureReason =
    (typeof SelectableTerminalAccessFailureReason)[keyof typeof SelectableTerminalAccessFailureReason];

export interface ListAccessibleTerminalsInfoRequest {
    system_id?: SupportedIpgSystemId;
    is_virtual: boolean;
    action?: AccessibleTerminalAuthorizationAction;
    resource_type?: AccessibleTerminalAuthorizationResourceType;
    limit?: number;
    cursor?: string;
}

export interface ListAccessibleCredentialsInfoRequest {
    system_id?: ThirdPartySystemId;
    is_virtual: boolean;
    credential_type: CredentialType;
    limit?: number;
    cursor?: string;
}

export const PaymentFeeDefinedBy = {
    TERMINAL_DEVICE: 'terminal_device',
    LOGICAL_TERMINAL: 'logical_terminal',
    MERCHANT_ACCOUNT: 'merchant_account',
    NOT_ALLOWED: 'not_allowed',
} as const;

export type PaymentFeeDefinedBy = (typeof PaymentFeeDefinedBy)[keyof typeof PaymentFeeDefinedBy];

export interface ListCardPaymentTerminalDevicesResponse {
    terminals: CardPaymentTerminalMenuItem[];
    pagination: {
        has_more: boolean;
        next_cursor?: string;
    };
    empty_reason?: SelectableTerminalAccessFailureReason;
}

export interface AccessibleCredentialInfoListItem {
    credential_uuid: string;
    organization_uuid: string;
    system_id: ThirdPartySystemId;
    account_id: string;
    credential_type: CredentialType;
    public_fields: Record<string, unknown>;
    status: Status;
    applies_to_children: boolean;
    linked_terminal_device_processor_device_id: string | null;
}

export interface ListAccessibleCredentialsInfoResponse {
    credentials: AccessibleCredentialInfoListItem[];
    pagination: {
        has_more: boolean;
        next_cursor?: string;
    };
}

export interface AccessibleTerminalInfoListItem {
    terminal_device_uuid: string;
    terminal_device_name: string | null;
    display_label: string;
    logical_terminal_uuid: string;
    merchant_account_uuid: string;
    processor_name: string;
    processor_mid: string;
    processor_tid: string;
    processor_device_id: string;
    system_id: SupportedIpgSystemId | null;
    credential_uuid: string | null;
    terminal_device_credential_type: CredentialType | null;
    linked_credential_type: CredentialType | null;
    qa_has_credential_link: boolean;
    qa_terminal_device_credential_type_set: boolean;
    qa_terminal_device_credential_type_matches_linked_credential_type: boolean;
    qa_terminal_virtuality_matches_linked_credential_type: boolean;
    effective_organization_uuid: string;
    effective_applies_to_children: boolean;
    effective_allow_override_fee: boolean;
    effective_surcharge_pct: number | null;
    effective_surcharge_defined_by: PaymentFeeDefinedBy;
    effective_convenience_fee: number | null;
    effective_convenience_fee_defined_by: PaymentFeeDefinedBy;
}

export interface ListAccessibleTerminalsInfoResponse {
    terminals: AccessibleTerminalInfoListItem[];
    pagination: {
        has_more: boolean;
        next_cursor?: string;
    };
}

/**
 * Process ACH Payment Request
 * Records a successful ACH payment processed by Valor REST API
 */
export interface ProcessAchPaymentRequest {
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
    gateway_txn_id?: string;
    gateway_request?: GatewayLifecycleRequest;
    gateway_response?: JsonValue;
    gateway_response_status?: number;
    gateway_response_header?: Record<string, string>;
}

/**
 * Process ACH Payment Response
 */
export interface ProcessAchPaymentResponse {
    success: boolean;
    payment_transaction_uuid: string;
}

/**
 * Payment Audit Log Entry
 */
export interface PaymentAuditLogEntry {
    payment_transaction_lifecycle_event_uuid: string;
    payment_transaction_uuid: string;
    event_type: PaymentTransactionLifecycleEventType;
    event_start: string;
    event_end: string;
    event_ts?: string;
    merchant_transaction_id: string;
    customer_name?: string;
    gateway_txn_id?: string;
    gateway_request?: GatewayLifecycleRequest;
    gateway_response_http_status?: number;
    gateway_response_header?: Record<string, string>;
    gateway_response_json?: GatewayLifecycleResponseJsonBody;
    gateway_response_raw?: string;
    error_code?: string;
    error_message?: string;
    pillar_request?: PillarPaymentRequest;
    pillar_response?: PillarPaymentResponse;
    client_request?: PillarPaymentRequest;
}

/**
 * Payment Audit Log Request
 */
export interface GetPaymentAuditLogRequest extends PaginationParams {
    ipw_system_id: ThirdPartySystemId;
    search?: string;
}

export interface GetPaymentAuditLogSearchRequest extends PaginationParams, SearchTermsRequest {
    ipw_system_id: ThirdPartySystemId;
}

/**
 * Payment Audit Log Response
 */
export interface GetPaymentAuditLogResponse {
    events: PaymentAuditLogEntry[];
    pagination: OpaqueCursorPagination;
}

/**
 * Get Open Batch Request
 * Returns list of transaction IDs in the open batch
 */
export interface GetOpenBatchRequest {
    account_id: string;
    ipg_system_id: IpgSystemId;
}

/**
 * Get Open Batch Response
 * Returns list of transaction IDs in the open batch
 */
export interface GetOpenBatchResponse {
    gateway_txn_ids: string[]; // List of gateway transaction IDs in open batch
    batch_no?: string; // Batch number
}

/**
 * Query Transactions Request
 * Queries Valor transactions with filters (for reconciliation)
 */
export interface QueryTransactionsRequest {
    ipg_system_id: ThirdPartySystemId;
    merchant_transaction_id?: string; // Filter by invoice number
    date_filter?: string; // "1" for today, "2" for yesterday, etc.
    amount?: string; // Filter by amount
    account_id: string; // Mnemonic ID (account_id) for the secret to use (required)
}

/**
 * Query Transactions Response
 */
export interface QueryTransactionsResponse {
    transactions: Array<{
        txnid: string;
        amount?: string;
        date?: string;
        merchant_transaction_id?: string;
    }>;
}

/**
 * Get Transaction Status Request
 * Gets status of a specific terminal transaction
 */
export interface GetTransactionStatusRequest {
    req_txn_id?: string; // Request transaction ID
    txn_id?: string; // Terminal transaction ID
    channel_id?: string;
    account_id: string; // Mnemonic ID (account_id) for the secret to use (required)
}

/**
 * Get Transaction Status Response
 */
export interface GetTransactionStatusResponse {
    state?: string; // Transaction state
    txn_id?: string; // Terminal transaction ID
    merchant_transaction_id?: string; // Merchant transaction ID
    amount?: string;
    error_code?: string;
    error_msg?: string;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
    page?: number; // Page number (1-indexed)
    limit?: number; // Items per page
    cursor?: string; // Cursor for cursor-based pagination
}

/**
 * Get Vault Transactions Request
 * Gets payment transactions from PILLAR vault
 */
export interface GetVaultTransactionsRequest extends PaginationParams {
    ipw_customer_id?: string; // IPW customer external ID (optional - if omitted, returns all transactions for the system)
    ipw_system_id?: ThirdPartySystemId; // Optional; omitted means all enabled IPW systems
    search?: string; // Optional search (customer name/last name prefix or invoice number)
    gateway_transaction_types?: GatewayTransactionType[];
}

export interface GetVaultTransactionsSearchRequest
    extends PaginationParams,
        SearchTermsRequest,
        StructuredSearchPredicatesRequest {
    ipw_customer_id?: string;
    ipw_system_id?: ThirdPartySystemId;
    gateway_transaction_types?: GatewayTransactionType[];
}

/**
 * Transaction List Item
 * Represents a transaction in the vault transaction list
 */
export interface VaultTransactionListItem {
    payment_transaction_uuid: string;
    merchant_organization_uuid: string;
    merchant_display_name: string;
    merchant_user_uuid: string;
    merchant_user_display_name: string;
    gateway_txn_id: string | null;
    transaction_type: PaymentTransactionFlowType;
    client_request_details?: TransactionDetails | null;
    customer_name: string | null;
    cardholder_name: string | null;
    card_last4: string | null;
    base_amount: string | null;
    original_amount: string;
    batched_amount: string | null;
    refunded_amount: string | null;
    status: PaymentTransactionStatus;
    reconciled: PaymentReconciliationStatus;
    merchant_transaction_id: string;
    transaction_created_ts: string; // ISO 8601 timestamp when transaction was created
    auth_decision_ts: string | null; // ISO 8601 timestamp when auth status is known
    is_voidable: boolean; // Whether transaction is in open batch
    is_refundable: boolean; // Whether transaction can be refunded (batched_amount > 0)
    can_display: boolean;
    data_quality: PaymentTransactionDataQuality | null;
}

export type PaymentTransactionDataQualitySeverity = 'warning' | 'error';

export interface PaymentTransactionDataQualityIssue {
    severity: PaymentTransactionDataQualitySeverity;
    message: string;
}

export interface PaymentTransactionDataQuality {
    severity: PaymentTransactionDataQualitySeverity;
    issues: PaymentTransactionDataQualityIssue[];
}

/**
 * Get Vault Transactions Response
 * Returns paginated list of transactions
 */
export interface GetVaultTransactionsResponse {
    transactions: VaultTransactionListItem[];
    pagination: {
        page?: number;
        limit?: number;
        total?: number;
        has_more: boolean;
        next_cursor?: string;
    };
}

export interface PaymentTransactionLifecycleEventListItem {
    payment_transaction_lifecycle_event_uuid: string;
    payment_transaction_uuid: string;
    merchant_transaction_id: string;
    event_type: PaymentTransactionLifecycleEventType;
    event_start: string;
    event_end: string;
    transaction_status?: PaymentTransactionStatus | null;
    reconciled?: PaymentReconciliationStatus | null;
    gateway_response_http_status?: number | null;
    gateway_stan?: string | null;
    gateway_rrn?: string | null;
    gateway_auth_code?: string | null;
    error_code?: string | null;
    error_message?: string | null;
}

export interface PaymentTransactionLifecycleEventSearchRequest
    extends PaginationParams,
        SearchTermsRequest,
        StructuredSearchPredicatesRequest {}

export interface PaymentTransactionLifecycleEventListResponse {
    lifecycle_events: PaymentTransactionLifecycleEventListItem[];
    pagination: OpaqueCursorPagination;
}

/**
 * Transaction Lifecycle Event
 * Represents a lifecycle event for a transaction
 */
export interface TransactionLifecycleEvent {
    payment_transaction_lifecycle_event_id: number;
    event_type: PaymentTransactionLifecycleEventType;
    event_start: string; // ISO 8601 timestamp
    event_end: string; // ISO 8601 timestamp
    event_ts?: string; // Legacy compatibility alias
    amount?: string; // Amount for void/refund events
    amounts?: JsonValue; // Raw amounts JSONB from lifecycle events
    error_code?: string;
    error_message?: string;
    gateway_request?: GatewayLifecycleRequest; // Full request JSON
    gateway_response_http_status?: number;
    gateway_response_header?: Record<string, string>;
    gateway_response_json?: GatewayLifecycleResponseJsonBody;
    gateway_response_raw?: string; // Raw response when JSON parsing failed
    gateway_request_type?: string;
    gateway_response_type?: string;
    gateway_operation_mnemonic?: string;
    gateway_endpoint_url?: string;
    pillar_request?: PillarPaymentRequest;
    pillar_response?: PillarPaymentResponse;
}

export interface PaymentTransactionAmountBreakdown {
    base_amount: number;
    surcharge?: number;
    convenience_fee?: number;
    tax?: number;
    tip?: number;
    donation?: number;
    total_amount: number;
}

export type AmountBreakdown = PaymentTransactionAmountBreakdown;

export const PaymentTransactionAmountBreakdownStorageVersion = {
    V2: 2,
    V3: 3,
} as const;

export type PaymentTransactionAmountBreakdownStorageVersion =
    (typeof PaymentTransactionAmountBreakdownStorageVersion)[keyof typeof PaymentTransactionAmountBreakdownStorageVersion];

export type LegacyPaymentTransactionAmountBreakdown = Omit<
    PaymentTransactionAmountBreakdown,
    'tip'
>;

export type PaymentTransactionAmountBreakdownStorageV1 =
    LegacyPaymentTransactionAmountBreakdown;

export interface PaymentTransactionAmountBreakdownStorageV2 {
    readonly payment_transaction_amount_breakdown_version:
        typeof PaymentTransactionAmountBreakdownStorageVersion.V2;
    readonly breakdown: LegacyPaymentTransactionAmountBreakdown;
}

export interface PaymentTransactionAmountBreakdownStorageV3 {
    readonly payment_transaction_amount_breakdown_version:
        typeof PaymentTransactionAmountBreakdownStorageVersion.V3;
    readonly breakdown: PaymentTransactionAmountBreakdown;
}

export type StoredPaymentTransactionAmountBreakdown =
    | PaymentTransactionAmountBreakdownStorageV1
    | PaymentTransactionAmountBreakdownStorageV2
    | PaymentTransactionAmountBreakdownStorageV3;

export const PaymentRequestAmountBreakdownStorageVersion = {
    V2: 2,
} as const;

export type PaymentRequestAmountBreakdownStorageVersion =
    (typeof PaymentRequestAmountBreakdownStorageVersion)[keyof typeof PaymentRequestAmountBreakdownStorageVersion];

export type PaymentRequestAmountBreakdownStorageV1 =
    LegacyPaymentTransactionAmountBreakdown;

export interface PaymentRequestAmountBreakdownStorageV2 {
    readonly payment_request_amount_breakdown_version:
        typeof PaymentRequestAmountBreakdownStorageVersion.V2;
    readonly amount_breakdown: LegacyPaymentTransactionAmountBreakdown;
}

export type StoredPaymentRequestAmountBreakdown =
    | PaymentRequestAmountBreakdownStorageV1
    | PaymentRequestAmountBreakdownStorageV2;

export interface PaymentRequestDocumentDescriptor {
    payment_request_document_uuid: string;
    source_kind: 'UPLOADED' | 'GENERATED';
    original_filename?: string;
    media_type: string;
    content_length_bytes: number;
    content_sha256: string;
    document_template_uuid?: string;
}

/**
 * Merchant-editable source text for the server-side accounting-document
 * templating system. The real safety boundary is server-side validation and
 * sanitization, not the TypeScript shape.
 */
export type DocumentTemplateSource = string;

export interface DocumentTemplateDescriptor {
    document_template_uuid: string;
    merchant_organization_uuid: string;
    name?: string;
    payment_request_details_type_id: string;
    payment_request_details_type_version: number;
    description?: string;
    content_sha256: string;
    status: Status;
}

export interface UploadPaymentRequestDocumentResponse {
    payment_request_document: PaymentRequestDocumentDescriptor;
}

export const PaymentRequestGeneratedDocumentKind = {
    CREDIT_CARD_RECEIPT: 'CREDIT_CARD_RECEIPT',
    INVOICE: 'INVOICE',
} as const;

export type PaymentRequestGeneratedDocumentKind =
    (typeof PaymentRequestGeneratedDocumentKind)[keyof typeof PaymentRequestGeneratedDocumentKind];

export type CreditCardReceiptTemplateSelection =
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.GENERIC;
          payment_request_details_type_version: 0;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_LEASING;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT;
          payment_request_details_type_id:
              typeof TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      };

export type InvoiceTemplateSelection =
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.INVOICE;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.INVOICE;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.INVOICE;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      }
    | {
          document_kind: typeof PaymentRequestGeneratedDocumentKind.INVOICE;
          payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_LEASING;
          payment_request_details_type_version: 1;
          document_template_uuid?: string;
      };

export type PaymentRequestDocumentTemplateSelection =
    | CreditCardReceiptTemplateSelection
    | InvoiceTemplateSelection;

export interface GeneratePaymentRequestDocumentRequest {
    payment_request_uuid: string;
    template_selection: PaymentRequestDocumentTemplateSelection;
}

export interface GeneratePaymentRequestDocumentResponse {
    payment_request_document: PaymentRequestDocumentDescriptor;
}

export interface DocumentGenerationMerchantBranding {
    legal_name?: string;
    dba_name?: string;
    address_lines: string[];
    phone_number?: string;
    css_stylesheet?: string;
    logo_asset_url?: string;
    anchor_href_allowlist: string[];
}

export interface DocumentGenerationPaymentRequestSnapshot {
    payment_request_uuid: string;
    title?: string;
    source_document_reference?: string;
    source_document_key?: PaymentRequestSourceDocumentKey;
    currency_code: string;
    original_amount?: number;
    amount_breakdown?: AmountBreakdown;
    recipient_email?: string;
    recipient_phone?: string;
    expires_ts?: string;
}

export interface CreditCardReceiptDocumentHydrationContext {
    template_selection: CreditCardReceiptTemplateSelection;
    merchant_branding: DocumentGenerationMerchantBranding;
    payment_request: DocumentGenerationPaymentRequestSnapshot;
    receipt: GetReceiptDataResponse;
    merchant_timezone: MerchantTimeZone;
    receipt_timestamp_display: string;
}

export interface DealertrackPartsInvoiceDocumentHydrationContext {
    template_selection: Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS }
    >;
    merchant_branding: DocumentGenerationMerchantBranding;
    payment_request: DocumentGenerationPaymentRequestSnapshot;
    payment_request_details: DealertrackPartsTransactionDetails;
}

export interface DealertrackServiceInvoiceDocumentHydrationContext {
    template_selection: Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE }
    >;
    merchant_branding: DocumentGenerationMerchantBranding;
    payment_request: DocumentGenerationPaymentRequestSnapshot;
    payment_request_details: DealertrackServiceTransactionDetails;
}

export interface DealertrackDealInvoiceDocumentHydrationContext {
    template_selection: Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL }
    >;
    merchant_branding: DocumentGenerationMerchantBranding;
    payment_request: DocumentGenerationPaymentRequestSnapshot;
    payment_request_details: DealertrackDealTransactionDetails;
}

export interface DealertrackLeasingInvoiceDocumentHydrationContext {
    template_selection: Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_LEASING }
    >;
    merchant_branding: DocumentGenerationMerchantBranding;
    payment_request: DocumentGenerationPaymentRequestSnapshot;
    payment_request_details: DealertrackLeasingTransactionDetails;
}

export type InvoiceDocumentHydrationContext =
    | DealertrackPartsInvoiceDocumentHydrationContext
    | DealertrackServiceInvoiceDocumentHydrationContext
    | DealertrackDealInvoiceDocumentHydrationContext
    | DealertrackLeasingInvoiceDocumentHydrationContext;

export type PaymentRequestDocumentHydrationContext =
    | CreditCardReceiptDocumentHydrationContext
    | InvoiceDocumentHydrationContext;

export interface EInvoiceEmailHydrationContext {
    merchant_branding: DocumentGenerationMerchantBranding;
    payment_request: DocumentGenerationPaymentRequestSnapshot;
    merchant_name: string;
    base_amount: number;
    payment_link: string;
    customer_name?: string;
    formatted_source_document_reference?: string;
}

export interface PaymentRequestDocumentReference {
    payment_request_document_uuid: string;
}

export interface CreateDraftPaymentRequestRequest {
    created_by_app: 'IPS' | 'PILLAR';
    created_by_ux_state?: string;
    terminal_device_uuid: string;
    merchant_customer_record_uuid?: string;
    merchant_user_uuid?: string;
    customer_organization_uuid?: string;
    customer_user_uuid?: string;
    source_ipw_system_id?: number;
    source_document_reference?: string;
    source_document_key?: PaymentRequestSourceDocumentKey;
    title?: string;
    message?: string;
    payment_request_details?: PaymentRequestDetails;
    original_amount?: number;
    amount_breakdown?: AmountBreakdown;
    currency_code: string;
    expires_ts?: string;
    recipient_email?: string;
    recipient_phone?: string;
    email_opt_in?: boolean;
    sms_opt_in?: boolean;
}

export interface CreateDraftPaymentRequestResponse {
    payment_request_uuid: string;
    status: PaymentRequestStatus;
}

export interface FinalizeSendPaymentRequestRequestBase {
    payment_request_uuid: string;
    allow_resend_existing_request?: boolean;
}

export interface ResendPaymentRequestRequestBase {
    send_email: boolean;
    send_sms: boolean;
}

export interface FinalizeSendPaymentRequestEmailOnlyRequest
    extends FinalizeSendPaymentRequestRequestBase {
    send_email: true;
    send_sms: false;
    recipient_email: string;
}

export interface FinalizeSendPaymentRequestSmsOnlyRequest
    extends FinalizeSendPaymentRequestRequestBase {
    send_email: false;
    send_sms: true;
    recipient_phone: string;
}

export interface FinalizeSendPaymentRequestEmailAndSmsRequest
    extends FinalizeSendPaymentRequestRequestBase {
    send_email: true;
    send_sms: true;
    recipient_email: string;
    recipient_phone: string;
}

export interface ResendPaymentRequestEmailOnlyRequest extends ResendPaymentRequestRequestBase {
    send_email: true;
    send_sms: false;
    recipient_email: string;
}

export interface ResendPaymentRequestSmsOnlyRequest extends ResendPaymentRequestRequestBase {
    send_email: false;
    send_sms: true;
    recipient_phone: string;
}

export interface ResendPaymentRequestEmailAndSmsRequest extends ResendPaymentRequestRequestBase {
    send_email: true;
    send_sms: true;
    recipient_email: string;
    recipient_phone: string;
}

export interface CancelAndReissuePaymentRequestRequestBase {
    send_email: boolean;
    send_sms: boolean;
}

export interface CancelAndReissuePaymentRequestEmailOnlyRequest
    extends CancelAndReissuePaymentRequestRequestBase {
    send_email: true;
    send_sms: false;
    recipient_email: string;
}

export interface CancelAndReissuePaymentRequestSmsOnlyRequest
    extends CancelAndReissuePaymentRequestRequestBase {
    send_email: false;
    send_sms: true;
    recipient_phone: string;
}

export interface CancelAndReissuePaymentRequestEmailAndSmsRequest
    extends CancelAndReissuePaymentRequestRequestBase {
    send_email: true;
    send_sms: true;
    recipient_email: string;
    recipient_phone: string;
}

export type FinalizeSendPaymentRequestRequest =
    | FinalizeSendPaymentRequestEmailOnlyRequest
    | FinalizeSendPaymentRequestSmsOnlyRequest
    | FinalizeSendPaymentRequestEmailAndSmsRequest;

export type ResendPaymentRequestRequest =
    | ResendPaymentRequestEmailOnlyRequest
    | ResendPaymentRequestSmsOnlyRequest
    | ResendPaymentRequestEmailAndSmsRequest;

export type CancelAndReissuePaymentRequestRequest =
    | CancelAndReissuePaymentRequestEmailOnlyRequest
    | CancelAndReissuePaymentRequestSmsOnlyRequest
    | CancelAndReissuePaymentRequestEmailAndSmsRequest;

export interface FinalizeSendPaymentRequestChannelDispatchResult {
    delivery_channel: DeliveryChannel;
    accepted: boolean;
    provider: DeliveryProvider;
    provider_message_id?: string;
    error_code?: string;
    error_message?: string;
}

export interface FinalizeSendPaymentRequestResponse {
    payment_request_uuid: string;
    status: PaymentRequestStatus;
    single_use_auth_token_uuid: string;
    single_use_auth_token_value: string;
    public_payment_url: string;
    payment_request_reused: boolean;
    channel_results: FinalizeSendPaymentRequestChannelDispatchResult[];
}

export interface CancelAndReissuePaymentRequestResponse
    extends FinalizeSendPaymentRequestResponse {
    superseded_payment_request_uuid: string;
}

export interface PublicPaymentLinkActionResponse {
    payment_request_uuid: string;
    channel_results: FinalizeSendPaymentRequestChannelDispatchResult[];
}

export const PublicPaymentRequestState = {
    ACTIVE: 'ACTIVE',
    EXPIRED: 'EXPIRED',
    CANCELED: 'CANCELED',
    PAID: 'PAID',
    REISSUED: 'REISSUED',
    ERROR: 'ERROR',
} as const;

export type PublicPaymentRequestState =
    (typeof PublicPaymentRequestState)[keyof typeof PublicPaymentRequestState];

export const PublicPaymentRequestErrorCode = {
    PAYMENT_REQUEST_LINK_INVALID: 'PAYMENT_REQUEST_LINK_INVALID',
    PAYMENT_REQUEST_LINK_UNAVAILABLE: 'PAYMENT_REQUEST_LINK_UNAVAILABLE',
} as const;

export type PublicPaymentRequestErrorCode =
    (typeof PublicPaymentRequestErrorCode)[keyof typeof PublicPaymentRequestErrorCode];

export interface PublicPaymentRequestDocumentView {
    payment_request_document_uuid: string;
    original_filename?: string;
    media_type: string;
}

export interface PublicResolvedPaymentRequestViewBase {
    payment_request_uuid: string;
    state:
        | typeof PublicPaymentRequestState.ACTIVE
        | typeof PublicPaymentRequestState.EXPIRED
        | typeof PublicPaymentRequestState.CANCELED
        | typeof PublicPaymentRequestState.PAID
        | typeof PublicPaymentRequestState.REISSUED;
    merchant_display_name: string;
    title?: string;
    source_document_reference?: string;
    source_document_key?: PaymentRequestSourceDocumentKey;
    original_amount?: number;
    amount_breakdown?: AmountBreakdown;
    currency_code: string;
    terminal_device_uuid?: string;
    documents: PublicPaymentRequestDocumentView[];
}

export interface PublicActivePaymentRequestView extends PublicResolvedPaymentRequestViewBase {
    state: typeof PublicPaymentRequestState.ACTIVE;
    expires_ts?: string;
}

export interface PublicExpiredPaymentRequestView extends PublicResolvedPaymentRequestViewBase {
    state: typeof PublicPaymentRequestState.EXPIRED;
    expires_ts?: string;
}

export interface PublicPaidPaymentRequestView extends PublicResolvedPaymentRequestViewBase {
    state: typeof PublicPaymentRequestState.PAID;
    fulfilled_ts?: string;
}

export interface PublicCanceledPaymentRequestView extends PublicResolvedPaymentRequestViewBase {
    state: typeof PublicPaymentRequestState.CANCELED;
}

export interface PublicReissuedPaymentRequestView extends PublicResolvedPaymentRequestViewBase {
    state: typeof PublicPaymentRequestState.REISSUED;
    replacement_payment_request_uuid?: string;
    replacement_public_payment_url?: string;
    replacement_expires_ts?: string;
}

export interface PublicErroredPaymentRequestView {
    state: typeof PublicPaymentRequestState.ERROR;
    error_code: PublicPaymentRequestErrorCode;
}

export type PublicPaymentRequestView =
    | PublicActivePaymentRequestView
    | PublicExpiredPaymentRequestView
    | PublicCanceledPaymentRequestView
    | PublicPaidPaymentRequestView
    | PublicReissuedPaymentRequestView
    | PublicErroredPaymentRequestView;

export interface GuestPaymentEntryContextView {
    payment_request_uuid: string;
    merchant_display_name: string;
    title?: string;
    source_document_reference?: string;
    source_document_key?: PaymentRequestSourceDocumentKey;
    transaction_details: TransactionDetails;
    original_amount?: number;
    amount_breakdown?: AmountBreakdown;
    currency_code: string;
    terminal_device_uuid?: string;
    receipt_email?: string;
    payment_configuration: PaymentConfigurationResponse;
    login_cta?: GuestPaymentEntryLoginCtaView;
}

export interface GuestPaymentEntryLoginCtaView {
    href: string;
    email: string;
}

export const GuestPaymentSuccessAccountCtaKind = {
    LOGIN: 'LOGIN',
    SIGNUP: 'SIGNUP',
} as const;

export type GuestPaymentSuccessAccountCtaKind =
    (typeof GuestPaymentSuccessAccountCtaKind)[keyof typeof GuestPaymentSuccessAccountCtaKind];

export interface GuestPaymentSuccessLoginAccountCtaView {
    kind: typeof GuestPaymentSuccessAccountCtaKind.LOGIN;
    href: string;
    email: string;
}

export interface GuestPaymentSuccessSignupAccountCtaView {
    kind: typeof GuestPaymentSuccessAccountCtaKind.SIGNUP;
    href: string;
    email?: string;
    payment_method_storage_available: boolean;
}

export type GuestPaymentSuccessAccountCtaView =
    | GuestPaymentSuccessLoginAccountCtaView
    | GuestPaymentSuccessSignupAccountCtaView;

export interface GuestPaymentSuccessView {
    payment_request_uuid: string;
    merchant_display_name: string;
    gateway_txn_id?: string;
    gateway_auth_code?: string;
    receipt_html_path?: string;
    account_cta?: GuestPaymentSuccessAccountCtaView;
}

export interface GuestPaymentRequestDocumentRedirectAccessResponse {
    kind: 'redirect';
    url: string;
}

export interface GuestPaymentRequestDocumentInlineAccessResponse {
    kind: 'inline';
    content_type: string;
    content_length?: number;
}

export type GuestPaymentRequestDocumentAccessResponse =
    | GuestPaymentRequestDocumentRedirectAccessResponse
    | GuestPaymentRequestDocumentInlineAccessResponse;

export type TwilioStatusCallbackPayload = Record<string, string | Array<string> | undefined>;

export type SendgridEventWebhookEvent = Record<string, unknown>;

export type SendgridEventWebhookPayload = ReadonlyArray<SendgridEventWebhookEvent>;

export interface AuthenticatedPaymentEntryContextView {
    payment_request_uuid: string;
    stored_payment_methods: CustomerPaymentMethodListItem[];
}

export interface PaymentRequestDeliveryEventView {
    payment_request_delivery_event_uuid: string;
    delivery_channel: DeliveryChannel;
    delivery_provider: DeliveryProvider;
    provider_event_type: string;
    provider_message_id?: string;
    provider_event_ts?: string;
    received_ts: string;
}

export interface PaymentRequestDeliveryEventDetailsView
    extends PaymentRequestDeliveryEventView {
    provider_payload?: PaymentRequestDeliveryEventPayload;
}

export interface PaymentRequestDeliveryEventList {
    payment_request_uuid: string;
    delivery_events: PaymentRequestDeliveryEventView[];
}

export interface PaymentRequestOwnerCandidate {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
}

export interface PaymentRequestOwnerCandidatesResponse {
    current_user_uuid: string;
    users: PaymentRequestOwnerCandidate[];
}

export interface PaymentRequestListItem {
    payment_request_uuid: string;
    merchant_organization_uuid: string;
    merchant_display_name: string;
    customer_organization_uuid?: string;
    merchant_customer_record_uuid?: string;
    status: PaymentRequestStatus;
    reconciled: PaymentReconciliationStatus | null;
    customer_name?: string;
    title?: string;
    recipient_email?: string;
    recipient_phone?: string;
    source_document_reference?: string;
    merchant_transaction_id?: string;
    source_document_key?: PaymentRequestSourceDocumentKey;
    base_amount?: number;
    original_amount?: number;
    currency_code: string;
    created_ts: string;
    expires_ts?: string;
    can_display: boolean;
}

export interface PaymentRequestList {
    payment_requests: PaymentRequestListItem[];
}

export const MerchantPaymentRequestStatusBucket = {
    ACTIVE: 'ACTIVE',
    PAID_TO_RECONCILE: 'PAID_TO_RECONCILE',
    COMPLETED: 'COMPLETED',
    REVOKED: 'REVOKED',
} as const;

export type MerchantPaymentRequestStatusBucket =
    (typeof MerchantPaymentRequestStatusBucket)[keyof typeof MerchantPaymentRequestStatusBucket];

export interface MerchantPaymentRequestSearchRequest
    extends SearchTermsRequest,
        StructuredSearchPredicatesRequest {
    statuses?: PaymentRequestStatus[];
    status_bucket?: MerchantPaymentRequestStatusBucket;
    require_reconcile_candidates?: boolean;
    payment_request_uuids?: string[];
    limit?: number;
    cursor?: number;
}

export interface MerchantPaymentRequestListResponse {
    payment_requests: PaymentRequestListItem[];
    pagination: CursorPagination;
}

export interface PaymentRequestDetailsView extends PaymentRequestListItem {
    message?: string;
    payment_request_details?: PaymentRequestDetails;
    amount_breakdown?: AmountBreakdown;
    public_payment_url?: string;
    documents: PaymentRequestDocumentDescriptor[];
    delivery_events: PaymentRequestDeliveryEventView[];
    payment_transactions: PaymentRequestTransactionListItem[];
}

export interface PaymentRequestTransactionListItem {
    payment_transaction_uuid: string;
    merchant_transaction_id?: string;
    original_amount?: number;
    currency_code?: string;
    status: PaymentTransactionStatus;
    created_ts: string;
    is_voidable: boolean;
    is_refundable: boolean;
    refundable_amount?: number;
    can_display: boolean;
}

export interface CustomerPortalDashboardRequest {
    merchant_organization_uuid?: string;
    customer_organization_uuid?: string;
}

export interface CustomerPortalDashboardMerchantOption {
    merchant_organization_uuid: string;
    merchant_display_name: string;
}

export interface CustomerPortalDashboardSummary {
    open_payment_request_count: number;
    overdue_payment_request_count: number;
    open_payment_request_total_amount?: number;
    currency_code?: string;
}

export interface CustomerPortalDashboardResponse {
    merchant_options: CustomerPortalDashboardMerchantOption[];
    selected_merchant_organization_uuid?: string;
    summary: CustomerPortalDashboardSummary;
    recent_payment_requests: PaymentRequestListItem[];
}

export interface CustomerPaymentRequestListRequest {
    merchant_organization_uuid?: string;
    customer_organization_uuid?: string;
    statuses?: PaymentRequestStatus[];
    limit?: number;
    cursor?: number;
}

export interface CustomerPaymentRequestSearchRequest extends SearchTermsRequest {
    merchant_organization_uuid?: string;
    customer_organization_uuid?: string;
    statuses?: PaymentRequestStatus[];
    limit?: number;
    cursor?: number;
}

export interface CustomerPaymentRequestListResponse {
    payment_requests: PaymentRequestListItem[];
    pagination: CursorPagination;
}

export interface CustomerPaymentHistoryRequest {
    merchant_organization_uuid?: string;
    customer_organization_uuid?: string;
    limit?: number;
    cursor?: number;
}

export interface CustomerPaymentHistoryListItem {
    payment_transaction_uuid: string;
    merchant_organization_uuid: string;
    merchant_display_name: string;
    payment_request_uuid?: string;
    amount: number;
    currency_code: string;
    payment_rail?: PaymentRail;
    payment_method_summary?: string;
    status: PaymentTransactionStatus;
    submitted_ts: string;
}

export interface CustomerPaymentHistoryResponse {
    payments: CustomerPaymentHistoryListItem[];
    pagination: CursorPagination;
}

/**
 * Get Transaction Details Response
 * Returns full transaction details with lifecycle events
 */
export interface GetTransactionDetailsResponse {
    payment_transaction_uuid: string;
    payment_request_uuid?: string;
    merchant_user_uuid: string;
    merchant_organization_uuid: string;
    merchant_organization_display_name?: string | null;
    merchant_timezone: MerchantTimeZone;
    terminal_device_uuid: string;
    processor_device_id: string;
    processor_tid?: string | null;
    terminal_device_name?: string | null;
    amount_breakdown: PaymentTransactionAmountBreakdown | null;
    ipw_transaction_details?: TransactionDetails | null;
    gateway_id: number; // GatewayId pseudo-enum
    gateway_transaction_type: GatewayTransactionType;
    gateway_txn_id?: string;
    gateway_batch?: string;
    gateway_auth_code?: string;
    gateway_stan?: string;
    gateway_rrn?: string;
    currency_code?: string;
    card_brand?: string;
    card_type?: CardType;
    card_last4?: string;
    cardholder_name?: string;
    original_amount: string;
    batched_amount: string | null;
    refunded_amount: string | null;
    status: PaymentTransactionStatus;
    reconciled: PaymentReconciliationStatus;
    ipw_system_id: number;
    ipw_customer_id?: string | null;
    transaction_created_ts: string;
    auth_decision_ts?: string | null;
    merchant_transaction_id: string;
    description?: string;
    lifecycle_events: TransactionLifecycleEvent[];
    gateway_request?: GatewayLifecycleRequest; // Full request JSON
    transaction_configuration?: PaymentTransactionConfiguration;
}

export interface GetReceiptDataResponse {
    merchant_transaction_id: string;
    receipt_timestamp: string;
    gateway_rrn?: string;
    gateway_auth_code?: string;
    card_brand?: string;
    card_type: CardType;
    card_last4?: string;
    surcharge_pct?: number;
    original_amount: string;
    amount_breakdown: PaymentTransactionAmountBreakdown;
    transactionDetails: TransactionDetails;
    business_details?: BusinessDetails;
    can_get_receipt: boolean;
    missing_required_fields: string[];
}

/**
 * Record Reconciliation Request
 * Records reconciliation results from RPA workflow
 */
export interface RecordReconciliationRequestBase {
    reconciled: boolean; // Whether reconciliation succeeded
    gateway_request?: GatewayLifecycleRequest; // Optional gateway request data
    gateway_response?: JsonValue; // Optional gateway response data
    gateway_response_status?: number; // Optional gateway HTTP status code
    gateway_response_header?: Record<string, string>; // Optional gateway response headers
    gateway_request_type?: string; // Optional gateway request type identifier
    gateway_response_type?: string; // Optional gateway response type identifier
    error_code?: string; // Optional error code if reconciliation failed
    error_message?: string; // Optional error message if reconciliation failed
}

export interface RecordReconciliationRequestSingle extends RecordReconciliationRequestBase {
    mode: 'single';
    payment_transaction_uuid: string;
}

export interface RecordReconciliationRequestBatch extends RecordReconciliationRequestBase {
    mode: 'batch';
    payment_transaction_uuids: string[];
}

export type RecordReconciliationRequest =
    | RecordReconciliationRequestSingle
    | RecordReconciliationRequestBatch;

/**
 * Record Reconciliation Response
 */
export interface RecordReconciliationResponse {
    success: boolean;
    payment_transaction_lifecycle_event_uuid?: string;
    payment_transaction_lifecycle_event_uuids?: string[];
    error?: string; // Error message if recording failed
}

/**
 * Record Payment Request Reconciliation Request
 * Records reconciliation results for merchant payment requests and
 * their dependent payment transactions.
 */
export interface RecordPaymentRequestReconciliationRequest {
    payment_request_uuids: string[];
    reconciled: boolean;
    gateway_request?: GatewayLifecycleRequest;
    gateway_response?: JsonValue;
    gateway_request_type?: string;
    gateway_response_type?: string;
    error_code?: string;
    error_message?: string;
}

/**
 * Record Payment Request Reconciliation Response
 */
export interface RecordPaymentRequestReconciliationResponse {
    success: boolean;
    payment_transaction_lifecycle_event_uuids?: string[];
    error?: string;
}

/**
 * Reconcile Payments Candidates Request
 */
export interface DetectedDetailsReconcilePaymentsCandidatesRequest {
    customer_name: string;
    ipw_system_id: ThirdPartySystemId;
    phone_number?: string;
    parts_invoice_number?: string;
    ro_number?: string;
    reconciled?: PaymentReconciliationStatus;
    limit?: number;
}

export interface ExactTransactionReconcilePaymentsCandidatesRequest {
    payment_transaction_uuid: string;
    ipw_system_id: ThirdPartySystemId;
    reconciled?: PaymentReconciliationStatus;
    limit?: number;
}

export type GetReconcilePaymentsCandidatesRequest =
    | DetectedDetailsReconcilePaymentsCandidatesRequest
    | ExactTransactionReconcilePaymentsCandidatesRequest;

export interface ReconcilePaymentsCandidate {
    payment_transaction_uuid: string;
    status: PaymentTransactionStatus;
    reconciled: PaymentReconciliationStatus;
    base_amount: string;
    original_amount: string;
    auth_decision_ts: string;
    merchant_transaction_id: string;
    customer_name?: string;
    gateway_txn_id?: string;
    phone_number?: string;
    ro_number?: string;
}

export interface GetReconcilePaymentsCandidatesResponse {
    transactions: ReconcilePaymentsCandidate[];
}

/**
 * Get reusable saved payment methods for a customer and selected terminal.
 */
export interface GetSavedCardPaymentMethodsRequest {
    customer_name: string;
    phone_number: string;
    ipw_system_id: ThirdPartySystemId;
    terminal_device_uuid: string;
}

/**
 * UUID and non-sensitive display metadata for a reusable payment method.
 */
export interface SavedCardPaymentMethod {
    payment_method_token_uuid: string;
    created_ts: string;
    masked_pan: string;
    card_type: CardType;
    card_brand: string;
    card_last4: string;
    cardholder_name: string;
    billingAddress?: Address;
}

/**
 * Saved payment methods available for selection.
 */
export interface GetSavedCardPaymentMethodsResponse {
    cards: SavedCardPaymentMethod[];
}

export interface CustomerPaymentMethodListRequest {
    customer_organization_uuid?: string;
}

export interface CustomerPaymentMethodListItem {
    payment_method_token_uuid: string;
    status: Status;
    created_ts: string;
    stored_for_future_use: boolean;
    payment_method_token_details: PaymentMethodTokenDetails;
}

export interface CustomerPaymentMethodListResponse {
    payment_methods: CustomerPaymentMethodListItem[];
}

export interface MerchantPaymentMethodTokenListRequest {
    limit?: number;
    cursor?: number;
}

export interface MerchantPaymentMethodTokenListItem {
    payment_method_token_uuid: string;
    holder_organization_uuid: string;
    holder_organization_name: string;
    created_ts: string;
    stored_for_future_use: boolean;
    payment_method_token_details: PaymentMethodTokenDetails;
    can_display: boolean;
    can_delete: boolean;
}

export interface MerchantPaymentMethodTokenListResponse {
    payment_method_tokens: MerchantPaymentMethodTokenListItem[];
    pagination: CursorPagination;
}

export interface MerchantPaymentMethodTokenDetailsView {
    payment_method_token_uuid: string;
    holder_organization_uuid: string;
    holder_organization_name: string;
    payment_rail: PaymentRail;
    gateway_id: GatewayId;
    gateway_token: string;
    created_by_transaction_uuid?: string;
    created_ts: string;
    stored_for_future_use: boolean;
    payment_method_token_details: PaymentMethodTokenDetails;
    customer_details?: CustomerDetails;
    can_delete: boolean;
}

export interface CustomerPaymentMethodBootstrapRequest {
    merchant_organization_uuid?: string;
    customer_organization_uuid?: string;
}

export interface CustomerPaymentMethodBootstrapResponse {
    merchant_organization_uuid: string;
    merchant_display_name: string;
    delegate_token: GetDelegateTokenResponse;
}

export interface CustomerPaymentMethodBinLookupRequest {
    merchant_organization_uuid: string;
    customer_organization_uuid?: string;
    pan_prefix: string;
}

export interface CreateCustomerPaymentMethodRequest {
    merchant_organization_uuid: string;
    customer_organization_uuid?: string;
    gateway_response: GatewayCardTokenizationResponse;
    card_brand: string;
    card_type: CardType;
    masked_pan: string;
    cardholder_name: string;
    customer_details?: CustomerDetails;
}

export interface CreateCustomerPaymentMethodResponse extends CustomerPaymentMethodListItem {}

export interface UpdateCustomerPaymentMethodRequest {
    payment_method_token_uuid: string;
    customer_organization_uuid?: string;
    stored_for_future_use: boolean;
}

export interface CustomerPaymentRequestDetailsRequest {
    customer_organization_uuid?: string;
}

export interface MerchantPaymentRequestDetailsRequest {
    merchant_organization_uuid?: string;
}

export interface UpdateCustomerPaymentMethodResponse {
    payment_method_token_uuid: string;
    stored_for_future_use: boolean;
}

/**
 * Get Delegate Token Request
 */
export interface GetDelegateTokenRequest {
    terminal_device_uuid: string;
}

/**
 * Get Delegate Token Response
 */
export type GetDelegateTokenResponse = IpgBrowserTokenizationSession;

export interface BinLookupRequest {
    terminal_device_uuid: string;
    pan_prefix: string;
}

export interface BinLookupResponse {
    card_type: CardType;
}

export interface PillarAchPaymentRequest {
    request_type: PillarPaymentRequestTypeMap['ACH_PAYMENT'];
    request: ProcessAchPaymentRequest;
    /** @deprecated Use request.transaction_details. */
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
}

export interface PillarProcessCardTokenPaymentRequest {
    request_type: PillarPaymentRequestTypeMap['PROCESS_CARD_TOKEN_PAYMENT'];
    request: ProcessCardTokenPaymentRequest;
    /** @deprecated Use request.transaction_details. */
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
}

export interface PillarProcessCardNotPresentPaymentRequest {
    request_type: PillarPaymentRequestTypeMap['PROCESS_CARD_NOT_PRESENT_PAYMENT'];
    request: ProcessCardNotPresentPaymentLifecycleRequest;
    /** @deprecated Use request.transaction_details. */
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
}

export interface PillarTerminalSaleRequest {
    request_type: PillarPaymentRequestTypeMap['TERMINAL_SALE'];
    request: TerminalSaleRequest;
    /** @deprecated Use request.transaction_details. */
    transaction_details: TransactionDetailsCreateRequest<TransactionDetails>;
}

export interface PillarTerminalAbortRequest {
    request_type: PillarPaymentRequestTypeMap['TERMINAL_ABORT'];
    request: TerminalAbortRequest;
}

export interface PillarVoidTransactionRequest {
    request_type: PillarPaymentRequestTypeMap['VOID_TRANSACTION'];
    request: VoidTransactionRequest;
}

export interface PillarRefundTransactionRequest {
    request_type: PillarPaymentRequestTypeMap['REFUND_TRANSACTION'];
    request: RefundTransactionRequest;
}

export interface PillarRecordTokenRequest {
    request_type: PillarPaymentRequestTypeMap['RECORD_TOKEN'];
    request: RecordTokenRequest;
}

export interface PillarGetOpenBatchRequest {
    request_type: PillarPaymentRequestTypeMap['GET_OPEN_BATCH'];
    request: GetOpenBatchRequest;
}

export interface PillarQueryTransactionsRequest {
    request_type: PillarPaymentRequestTypeMap['QUERY_TRANSACTIONS'];
    request: QueryTransactionsRequest;
}

export interface PillarGetTransactionStatusRequest {
    request_type: PillarPaymentRequestTypeMap['GET_TRANSACTION_STATUS'];
    request: GetTransactionStatusRequest;
}

export interface PillarGetVaultTransactionsRequest {
    request_type: PillarPaymentRequestTypeMap['GET_VAULT_TRANSACTIONS'];
    request: GetVaultTransactionsRequest;
}

export interface PillarGetTransactionDetailsRequest {
    request_type: PillarPaymentRequestTypeMap['GET_TRANSACTION_DETAILS'];
    payment_transaction_id: number;
}

export interface PillarRecordReconciliationRequest {
    request_type: PillarPaymentRequestTypeMap['RECORD_RECONCILIATION'];
    request: RecordReconciliationRequest;
}

export interface PillarPaymentAuditLogRequest {
    request_type: PillarPaymentRequestTypeMap['PAYMENT_AUDIT_LOG'];
    request: GetPaymentAuditLogRequest;
}

export type PillarPaymentRequest =
    | PillarTerminalSaleRequest
    | PillarTerminalAbortRequest
    | PillarVoidTransactionRequest
    | PillarRefundTransactionRequest
    | PillarAchPaymentRequest
    | PillarRecordTokenRequest
    | PillarProcessCardTokenPaymentRequest
    | PillarProcessCardNotPresentPaymentRequest
    | PillarGetOpenBatchRequest
    | PillarQueryTransactionsRequest
    | PillarGetTransactionStatusRequest
    | PillarGetVaultTransactionsRequest
    | PillarGetTransactionDetailsRequest
    | PillarRecordReconciliationRequest
    | PillarPaymentAuditLogRequest;

/**
 * The implicit unmarked V1 representation stored in lifecycle events.
 */
export type PillarPaymentRequestStorageV1 = PillarPaymentRequest;

export interface PillarTerminalSaleResponse {
    response_type: PillarPaymentResponseTypeMap['TERMINAL_SALE'];
    response: TerminalSaleResponse;
}

export interface PillarTerminalAbortResponse {
    response_type: PillarPaymentResponseTypeMap['TERMINAL_ABORT'];
    response: TerminalAbortResponse;
}

export interface PillarVoidTransactionResponse {
    response_type: PillarPaymentResponseTypeMap['VOID_TRANSACTION'];
    response: VoidTransactionResponse;
}

export interface PillarRefundTransactionResponse {
    response_type: PillarPaymentResponseTypeMap['REFUND_TRANSACTION'];
    response: RefundTransactionResponse;
}

export interface PillarAchPaymentResponse {
    response_type: PillarPaymentResponseTypeMap['ACH_PAYMENT'];
    response: ProcessAchPaymentResponse;
}

export interface PillarRecordTokenResponse {
    response_type: PillarPaymentResponseTypeMap['RECORD_TOKEN'];
    response: RecordTokenResponse;
}

export interface PillarProcessCardTokenPaymentResponse {
    response_type: PillarPaymentResponseTypeMap['PROCESS_CARD_TOKEN_PAYMENT'];
    response: ProcessCardTokenPaymentResponse;
}

export interface PillarProcessCardNotPresentPaymentResponse {
    response_type: PillarPaymentResponseTypeMap['PROCESS_CARD_NOT_PRESENT_PAYMENT'];
    response: ProcessCardNotPresentPaymentResponse;
}

export interface PillarGetOpenBatchResponse {
    response_type: PillarPaymentResponseTypeMap['GET_OPEN_BATCH'];
    response: GetOpenBatchResponse;
}

export interface PillarQueryTransactionsResponse {
    response_type: PillarPaymentResponseTypeMap['QUERY_TRANSACTIONS'];
    response: QueryTransactionsResponse;
}

export interface PillarGetTransactionStatusResponse {
    response_type: PillarPaymentResponseTypeMap['GET_TRANSACTION_STATUS'];
    response: GetTransactionStatusResponse;
}

export interface PillarGetVaultTransactionsResponse {
    response_type: PillarPaymentResponseTypeMap['GET_VAULT_TRANSACTIONS'];
    response: GetVaultTransactionsResponse;
}

export interface PillarGetTransactionDetailsResponse {
    response_type: PillarPaymentResponseTypeMap['GET_TRANSACTION_DETAILS'];
    response: GetTransactionDetailsResponse;
}

export interface PillarRecordReconciliationResponse {
    response_type: PillarPaymentResponseTypeMap['RECORD_RECONCILIATION'];
    response: RecordReconciliationResponse;
}

export interface PillarPaymentAuditLogResponse {
    response_type: PillarPaymentResponseTypeMap['PAYMENT_AUDIT_LOG'];
    response: GetPaymentAuditLogResponse;
}

export type PillarPaymentResponse =
    | PillarTerminalSaleResponse
    | PillarTerminalAbortResponse
    | PillarVoidTransactionResponse
    | PillarRefundTransactionResponse
    | PillarAchPaymentResponse
    | PillarRecordTokenResponse
    | PillarProcessCardTokenPaymentResponse
    | PillarProcessCardNotPresentPaymentResponse
    | PillarGetOpenBatchResponse
    | PillarQueryTransactionsResponse
    | PillarGetTransactionStatusResponse
    | PillarGetVaultTransactionsResponse
    | PillarGetTransactionDetailsResponse
    | PillarRecordReconciliationResponse
    | PillarPaymentAuditLogResponse;
